import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  Plus,
  Trash2,
  Globe,
  Database,
  Shield,
  Search,
  ChevronDown,
  Edit2,
  ArrowLeft,
  Code,
  Wrench,
  Info,
  Play,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  X,
  Cpu,
  Layers,
  KeyRound
} from "lucide-react";
import apiClient from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useToast } from "../context/ToastContext";

const CustomSelect = ({ value, options, onChange, label, className = "", disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div className={`relative ${className} ${disabled ? "opacity-50 pointer-events-none" : ""} ${isOpen ? "z-[50]" : "z-10"}`} ref={containerRef}>
      {label && <label className="block text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1 ml-0.5">{label}</label>}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2 text-xs border rounded-lg transition-all font-medium h-[38px] ${
          isOpen
            ? 'bg-white border-zinc-900 shadow-xs ring-1 ring-zinc-900/5 text-zinc-900'
            : 'bg-white border-zinc-200 text-zinc-800 hover:border-zinc-300'
        }`}
      >
        <span className="truncate">{selectedOption ? selectedOption.label : "Select"}</span>
        <ChevronDown size={13} className={`text-zinc-400 transition-transform duration-200 shrink-0 ml-2 ${isOpen ? 'rotate-180 text-zinc-900' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-1.5 bg-white border border-zinc-200 rounded-lg shadow-lg z-[100] max-h-56 overflow-y-auto custom-scrollbar py-1 animate-fadeIn">
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`px-3 py-2 text-xs cursor-pointer transition-colors flex items-center justify-between ${
                value === opt.value
                  ? "bg-zinc-50 font-bold text-zinc-900"
                  : "text-zinc-600 hover:bg-zinc-50/70 hover:text-zinc-900"
              }`}
            >
              <span>{opt.label}</span>
              {value === opt.value && <div className="w-1.5 h-1.5 rounded-full bg-zinc-900 shrink-0" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ParameterRow = ({ parameter, path, onUpdate, onRemove, level = 0 }) => {
  const isObject = parameter.type === "object";
  const isArray = parameter.type === "array";

  const handleUpdate = (updates) => {
    onUpdate(path, { ...parameter, ...updates });
  };

  const addSubProperty = () => {
    const newProperties = [...(parameter.properties || []), {
      name: "",
      type: "string",
      location: "body",
      value_type: "llm",
      required: true,
      description: ""
    }];
    handleUpdate({ properties: newProperties });
  };

  const addArrayProperty = () => {
    const newItems = parameter.items || { type: "object", properties: [] };
    const newProperties = [...(newItems.properties || []), {
      name: "",
      type: "string",
      location: "body",
      value_type: "llm",
      required: true,
      description: ""
    }];
    handleUpdate({ items: { ...newItems, properties: newProperties } });
  };

  return (
    <div className={`space-y-4 ${level > 0 ? "ml-2 sm:ml-6 pl-2 sm:pl-4 border-l-2 border-zinc-200 mt-3" : ""}`}>
      <div className="bg-white p-3.5 sm:p-5 rounded-xl border border-zinc-200/90 shadow-xs hover:border-zinc-300 transition-all group relative">
        <div className="space-y-4 relative z-10 hover:z-[30] focus-within:z-[40] transition-all">
          {/* Row 1: Core Identity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-3 sm:gap-4">
            <div className="sm:col-span-2 md:col-span-5">
              <label className="block text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1 ml-0.5">
                Identifier Name <span className="text-rose-500">*</span>
              </label>
              <input
                placeholder="e.g. user_id"
                value={parameter.name}
                onChange={(e) => handleUpdate({ name: e.target.value })}
                className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg focus:border-zinc-900 focus:outline-none bg-white transition-colors text-zinc-850 font-medium h-[38px]"
              />
            </div>
            <div className="sm:col-span-1 md:col-span-3">
              <CustomSelect
                label="Type"
                value={parameter.type}
                onChange={(val) => handleUpdate({
                  type: val,
                  properties: val === 'object' ? [] : undefined,
                  location: (val === 'object' && parameter.location === 'path') ? 'body' : parameter.location
                })}
                options={[
                  { label: "String", value: "string" },
                  { label: "Number", value: "number" },
                  { label: "Integer", value: "integer" },
                  { label: "Boolean", value: "boolean" },
                  { label: "Object", value: "object" },
                  { label: "Array", value: "array" }
                ]}
              />
            </div>
            <div className="sm:col-span-1 md:col-span-2">
              <CustomSelect
                label="Location"
                value={parameter.location || "body"}
                onChange={(val) => handleUpdate({ location: val })}
                options={[
                  { label: "Body", value: "body" },
                  { label: "Query", value: "query" },
                  ...(isObject ? [] : [{ label: "Path", value: "path" }])
                ]}
              />
            </div>
            <div className="sm:col-span-2 md:col-span-2">
              <CustomSelect
                label="Extraction"
                value={isObject ? "llm" : (parameter.value_type || "llm")}
                disabled={isObject}
                onChange={(val) => handleUpdate({ value_type: val })}
                options={[
                  { label: "LLM", value: "llm" },
                  { label: "Constant", value: "constant" }
                ]}
              />
            </div>
          </div>

          {/* Row 2: Logic & Instructions */}
          {!isObject && (
            <div>
              {parameter.value_type === "constant" ? (
                <div className="animate-fadeIn">
                  <label className="block text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1 ml-0.5">
                    Constant Value (Injected Automatically)
                  </label>
                  <input
                    placeholder="Enter fixed value..."
                    value={parameter.constant_value || ""}
                    onChange={(e) => handleUpdate({ constant_value: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg focus:border-zinc-900 focus:outline-none bg-white transition-colors text-zinc-850 font-medium h-[38px]"
                  />
                </div>
              ) : (
                <div className="animate-fadeIn">
                  <label className="block text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1 ml-0.5">
                    Description (Instruction for AI)
                  </label>
                  <textarea
                    placeholder="Explain to the AI how to extract or use this value..."
                    value={parameter.description || ""}
                    onChange={(e) => handleUpdate({ description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg focus:border-zinc-900 focus:outline-none bg-white transition-colors text-zinc-850 font-medium resize-y min-h-[38px] leading-relaxed"
                  />
                </div>
              )}
            </div>
          )}

          <div className="pt-2.5 flex flex-wrap items-center justify-between border-t border-zinc-100 gap-2.5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Required</span>
              <button
                type="button"
                onClick={() => handleUpdate({ required: !parameter.required })}
                className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 cursor-pointer ${parameter.required ? "bg-zinc-900" : "bg-zinc-200"}`}
              >
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${parameter.required ? "translate-x-4" : "translate-x-0"}`} />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 ml-auto">
              {isObject && (
                <button
                  type="button"
                  onClick={addSubProperty}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-zinc-50 hover:bg-zinc-100 text-zinc-700 rounded-lg transition-all font-semibold text-[10px] border border-zinc-200"
                >
                  <Plus size={12} /> Add Property
                </button>
              )}
              {isArray && (
                <button
                  type="button"
                  onClick={addArrayProperty}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-zinc-50 hover:bg-zinc-100 text-zinc-700 rounded-lg transition-all font-semibold text-[10px] border border-zinc-200"
                >
                  <Plus size={12} /> Add Array Item Property
                </button>
              )}
              <button
                type="button"
                onClick={() => onRemove(path)}
                className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                title="Remove parameter"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {isObject && (
        <div className="animate-fadeIn">
          {parameter.properties?.map((prop, idx) => (
            <ParameterRow
              key={idx}
              parameter={prop}
              path={[...path, 'properties', idx]}
              onUpdate={onUpdate}
              onRemove={onRemove}
              level={level + 1}
            />
          ))}
          <button
            type="button"
            onClick={addSubProperty}
            className="ml-4 sm:ml-6 mt-2 text-xs font-medium text-zinc-500 hover:text-zinc-900 transition-colors flex items-center gap-1.5 py-1.5 px-3 rounded-lg hover:bg-zinc-100 border border-zinc-200 bg-white"
          >
            <Plus size={13} />
            <span>Add Property to {parameter.name || 'Object'}</span>
          </button>
        </div>
      )}
      {isArray && (
        <div className="animate-fadeIn">
          {parameter.items?.properties?.map((prop, idx) => (
            <ParameterRow
              key={idx}
              parameter={prop}
              path={[...path, 'items', 'properties', idx]}
              onUpdate={onUpdate}
              onRemove={onRemove}
              level={level + 1}
            />
          ))}
          <button
            type="button"
            onClick={addArrayProperty}
            className="ml-4 sm:ml-6 mt-2 text-xs font-medium text-zinc-500 hover:text-zinc-900 transition-colors flex items-center gap-1.5 py-1.5 px-3 rounded-lg hover:bg-zinc-100 border border-zinc-200 bg-white"
          >
            <Plus size={13} />
            <span>Add Array Item Property to {parameter.name || 'Array'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

const ApiToolsManagement = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTool, setEditingTool] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isJsonMode, setIsJsonMode] = useState(false);
  const [jsonContent, setJsonContent] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [toolToDelete, setToolToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testInputs, setTestInputs] = useState({});
  const [arrayLengths, setArrayLengths] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [isTestLoading, setIsTestLoading] = useState(false);
  const [isRawRequestOpen, setIsRawRequestOpen] = useState(false);
  const [jsonError, setJsonError] = useState(null);
  const [initialParams, setInitialParams] = useState([]);

  // MCP State
  const [isMcpMode, setIsMcpMode] = useState(false);
  const [mcpServers, setMcpServers] = useState([]);
  const [isAddingMcp, setIsAddingMcp] = useState(false);
  const [mcpDiscovery, setMcpDiscovery] = useState({ url: "", headers: "{}", tools: [] });
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [uiError, setUiError] = useState(null);

  const emptyTool = {
    name: "",
    description: "",
    url: "",
    method: "GET",
    headers: "{}",
    parameters: [],
    otp_enabled: false,
    otp_config: {
      digits: 4,
      verify_url: "",
      verify_headers: {},
      verify_body_template: {
        otp: "{{otp}}"
      },
      context_mappings: {},
      verify_success: {
        type: "json_path",
        path: "success",
        expected: true
      },
      max_attempts: 3
    },
    is_active: true
  };

  const fetchTools = useCallback(async () => {
    try {
      const response = await apiClient.getTools();
      const toolsArray = Array.isArray(response) ? response : (response?.tools || []);
      setTools(toolsArray);
    } catch (error) {
      console.error("Error fetching tools:", error);
      setTools([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMcpServers = useCallback(async () => {
    try {
      const response = await apiClient.get("/mcp/servers", {
        headers: { "X-AI-Agent-ID": activeAiAgentId }
      });
      setMcpServers(response || []);
    } catch (error) {
      console.error("Error fetching MCP servers:", error);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    if (activeAiAgentId) {
      fetchTools();
      fetchMcpServers();
    }
  }, [activeAiAgentId, fetchTools, fetchMcpServers]);

  // Real-time JSON Validation & Strict Schema Check
  useEffect(() => {
    if (isJsonMode && jsonContent) {
      try {
        const parsed = JSON.parse(jsonContent);
        setJsonError(null);

        // 1. Root Level Validation
        const allowedRootKeys = ['name', 'description', 'method', 'url', 'headers', 'parameters', 'otp_enabled', 'otp_config', 'is_active'];
        Object.keys(parsed).forEach(key => {
          if (!allowedRootKeys.includes(key)) {
            throw new Error(`Unknown top-level property: "${key}". Available: ${allowedRootKeys.join(', ')}`);
          }
        });

        // 2. Global Safety Protection (Existing Parameter Stability)
        if (parsed.parameters && Array.isArray(parsed.parameters)) {
          const protectedFields = ['name', 'type', 'location', 'required'];
          const validTypes = ['string', 'number', 'integer', 'boolean', 'object', 'array'];
          const validLocations = ['query', 'path', 'body'];
          const validValueTypes = ['llm', 'constant'];

          const validateParamRecursive = (param, pNum, isNested = false) => {
            // Check for Unknown Parameter Keys
            const allowedKeys = ['name', 'type', 'location', 'value_type', 'required', 'constant_value', 'description', 'properties', 'items'];
            Object.keys(param).forEach(k => {
              if (!allowedKeys.includes(k)) {
                throw new Error(`${isNested ? 'Nested parameter' : `Parameter #${pNum}`}: Unknown property "${k}" found.`);
              }
            });

            // Validate Values
            if (param.type && !validTypes.includes(param.type))
              throw new Error(`${isNested ? 'Nested parameter' : `Parameter #${pNum}`}: "${param.type}" is invalid. Must be one of: ${validTypes.join(', ')}`);

            if (!isNested && param.location && !validLocations.includes(param.location))
              throw new Error(`Parameter #${pNum}: "${param.location}" is invalid. Must be 'query', 'path', or 'body'`);

            if (param.value_type && !validValueTypes.includes(param.value_type))
              throw new Error(`${isNested ? 'Nested parameter' : `Parameter #${pNum}`}: "value_type" must be 'llm' or 'constant'`);

            if (param.required !== undefined && typeof param.required !== 'boolean')
              throw new Error(`${isNested ? 'Nested parameter' : `Parameter #${pNum}`}: "required" must be a boolean (true or false)`);

            if (param.properties !== undefined) {
              if (!Array.isArray(param.properties)) {
                throw new Error(`${isNested ? 'Nested parameter' : `Parameter #${pNum}`}: "properties" must be an array`);
              }
              param.properties.forEach((subParam, idx) => {
                validateParamRecursive(subParam, `${pNum}.${idx + 1}`, true);
              });
            }

            if (param.items !== undefined) {
              if (typeof param.items !== 'object' || Array.isArray(param.items)) {
                throw new Error(`${isNested ? 'Nested parameter' : `Parameter #${pNum}`}: "items" must be an object`);
              }
              validateParamRecursive(param.items, `${pNum}.items`, true);
            }
          };

          parsed.parameters.forEach((current, i) => {
            const pNum = i + 1;

            validateParamRecursive(current, pNum, false);

            // Protection validation for existing items
            if (i < initialParams.length) {
              const original = initialParams[i];
              protectedFields.forEach(field => {
                if (current[field] !== original[field]) {
                  throw new Error(`Safety Violation: Field "${field}" in existing parameter #${pNum} cannot be modified from "${original[field]}" to "${current[field]}".`);
                }
              });
            }
          });
        }
      } catch (err) {
        // Strict Error Reporting: Ensure no truncation by browser or logic
        let msg = err.message || "Unknown validation exception";

        // Custom formatting for common JSON syntax errors to make them readable
        if (msg.includes("JSON.parse")) {
          msg = `JSON Syntax Violation: ${msg.replace("JSON.parse: ", "")}`;
        }

        setJsonError(msg);
      }
    } else {
      setJsonError(null);
    }
  }, [jsonContent, isJsonMode, initialParams]);



  const handleDiscoverMcp = async () => {
    if (!mcpDiscovery.url) return;
    setIsDiscovering(true);
    try {
      const headers = typeof mcpDiscovery.headers === 'string'
        ? JSON.parse(mcpDiscovery.headers || "{}")
        : mcpDiscovery.headers;

      const response = await apiClient.post("/mcp/discover", {
        url: mcpDiscovery.url,
        headers: headers
      });
      setMcpDiscovery(prev => ({
        ...prev,
        tools: (response.tools || []).map(name => ({ tool_name: name, is_active: true }))
      }));
      setUiError(null);
    } catch (error) {
      setUiError("Discovery Failure: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleSaveMcp = async () => {
    try {
      const headers = typeof mcpDiscovery.headers === 'string'
        ? JSON.parse(mcpDiscovery.headers || "{}")
        : mcpDiscovery.headers;

      await apiClient.post("/mcp/servers", {
        url: mcpDiscovery.url,
        headers: headers,
        tools: mcpDiscovery.tools.filter(t => t.is_active).map(t => t.tool_name)
      });
      setIsAddingMcp(false);
      setMcpDiscovery({ url: "", headers: "{}", tools: [] });
      setUiError(null);
      fetchMcpServers();
    } catch (error) {
      setUiError("Synchronization Error: " + (error.response?.data?.detail || error.message));
    }
  };

  const toggleMcpServer = async (serverId, currentStatus) => {
    try {
      await apiClient.put(`/mcp/servers/${serverId}`, { is_active: !currentStatus });
      fetchMcpServers();
    } catch (error) {
      console.error("Error toggling MCP server:", error);
    }
  };

  const toggleMcpTool = async (toolId, currentStatus) => {
    try {
      await apiClient.put(`/mcp/tools/${toolId}`, { is_active: !currentStatus });
      fetchMcpServers();
    } catch (error) {
      console.error("Error toggling MCP tool:", error);
    }
  };

  const [mcpServerToDelete, setMcpServerToDelete] = useState(null);
  const [isDeletingMcp, setIsDeletingMcp] = useState(false);

  const deleteMcpServer = (serverId) => {
    setMcpServerToDelete(serverId);
  };

  const confirmDeleteMcp = async () => {
    if (!mcpServerToDelete) return;
    setIsDeletingMcp(true);
    try {
      await apiClient.delete(`/mcp/servers/${mcpServerToDelete}`);
      fetchMcpServers();
      setMcpServerToDelete(null);
    } catch (error) {
      console.error("Error deleting MCP server:", error);
    } finally {
      setIsDeletingMcp(false);
    }
  };

  const handleSave = async (toolData) => {
    try {
      let finalizedData = { ...toolData };

      if (isJsonMode) {
        try {
          finalizedData = JSON.parse(jsonContent);
        } catch (e) {
          setUiError("Critical: Invalid JSON structure. Please rectify syntax.");
          return;
        }
      }

      // Headers must be a dictionary
      if (typeof finalizedData.headers === 'string') {
        try {
          finalizedData.headers = JSON.parse(finalizedData.headers || '{}');
        } catch (e) {
          setUiError("Header Violation: Invalid JSON in headers.");
          return;
        }
      }

      // Parameters must be a list
      if (typeof finalizedData.parameters === 'string') {
        try {
          finalizedData.parameters = JSON.parse(finalizedData.parameters || '[]');
        } catch (e) {
          setUiError("Parameter Violation: Invalid JSON in parameters.");
          return;
        }
      }

      finalizedData.otp_enabled = Boolean(finalizedData.otp_enabled);
      if (finalizedData.otp_config) {
        let conf = finalizedData.otp_config;
        if (typeof conf === 'string') {
          try { conf = JSON.parse(conf); } catch (e) { conf = {}; }
        }
        if (typeof conf?.verify_headers === 'string') {
          try { conf.verify_headers = JSON.parse(conf.verify_headers || '{}'); } catch (e) { conf.verify_headers = {}; }
        }
        if (typeof conf?.context_mappings === 'string') {
          try { conf.context_mappings = JSON.parse(conf.context_mappings || '{}'); } catch (e) { conf.context_mappings = {}; }
        }
        if (typeof conf?.verify_body_template === 'string') {
          try { conf.verify_body_template = JSON.parse(conf.verify_body_template); } catch (e) {}
        }
        finalizedData.otp_config = conf;
      }

      if (editingTool?.id) {
        await apiClient.updateTool(editingTool.id, finalizedData);
        showToast("Capability synchronized successfully", "success");
      } else {
        const response = await apiClient.createTool(finalizedData);
        finalizedData.id = response.id;
        showToast("Capability launched successfully", "success");
      }

      // Update local state and keep the editor open on the visual UI
      await fetchTools();
      setEditingTool({
        ...finalizedData,
        parameters: typeof finalizedData.parameters === 'string' ? JSON.parse(finalizedData.parameters || '[]') : (finalizedData.parameters || [])
      });
      setIsAdding(false);
      setIsJsonMode(false);
      setUiError(null);
    } catch (err) {
      console.error("Save Error:", err);
      setUiError("Storage Failure: " + err.message);
    }
  };

  const handleDelete = (id) => {
    setToolToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!toolToDelete) return;
    setIsDeleting(true);
    try {
      await apiClient.deleteTool(toolToDelete);
      fetchTools();
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting tool:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggle = async (tool) => {
    try {
      const newStatus = !tool.is_active;
      await apiClient.toggleTool(tool.id, newStatus);
      showToast(`Extension ${newStatus ? 'activated' : 'dismantled'} successfully`, "success");
      fetchTools();
    } catch (error) {
      console.error("Error toggling tool status:", error);
    }
  };

  const handleEdit = (tool) => {
    let params = tool.parameters;
    if (typeof params === 'string') {
      try { params = JSON.parse(params); } catch { params = []; }
    }

    setEditingTool({
      ...tool,
      url: tool.url,
      parameters: params || [],
      headers: tool.headers || {}
    });
    setIsJsonMode(false); // Always start in Visual UI mode
    setIsAdding(false);
    setTestInputs({});
    setTestResult(null);
  };

  const handleUpdateParameter = (path, updated) => {
    const newParams = JSON.parse(JSON.stringify(editingTool.parameters || []));
    let current = newParams;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    current[path[path.length - 1]] = updated;
    setEditingTool({ ...editingTool, parameters: newParams });
  };

  const handleRemoveParameter = (path) => {
    const newParams = JSON.parse(JSON.stringify(editingTool.parameters || []));
    let current = newParams;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    current.splice(path[path.length - 1], 1);
    setEditingTool({ ...editingTool, parameters: newParams });
  };

  const addParameter = () => {
    const currentTool = editingTool || emptyTool;
    const newParams = [...(currentTool.parameters || []), {
      name: "",
      type: "string",
      location: "body",
      value_type: "llm",
      required: true,
      description: ""
    }];
    setEditingTool({ ...currentTool, parameters: newParams });
  };

  const handleMethodChange = (newMethod) => {
    const currentTool = editingTool || emptyTool;
    let newParams = [...(currentTool.parameters || [])];

    if ((newMethod === "POST" || newMethod === "PUT") && newParams.length === 0) {
      newParams.push({
        name: "",
        type: "string",
        location: "body",
        value_type: "llm",
        required: true,
        description: ""
      });
    }

    setEditingTool({ ...currentTool, method: newMethod, parameters: newParams });
  };

  const getHostname = (url) => {
    try {
      return new URL(url).hostname;
    } catch {
      return url || "N/A";
    }
  };

  const filteredTools = (Array.isArray(tools) ? tools : []).filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const serializeTool = (tool) => {
    const formatted = {
      name: tool.name,
      description: tool.description,
      method: tool.method,
      url: tool.url,
      headers: typeof tool.headers === 'string' ? JSON.parse(tool.headers || '{}') : (tool.headers || {}),
      parameters: typeof tool.parameters === 'string' ? JSON.parse(tool.parameters || '[]') : (tool.parameters || []),
      otp_enabled: Boolean(tool.otp_enabled),
      otp_config: tool.otp_config || {},
      is_active: tool.is_active
    };
    return JSON.stringify(formatted, null, 2);
  };

  const handleToggleJsonMode = () => {
    if (!isJsonMode) {
      // Switching TO JSON mode
      const tool = editingTool || emptyTool;
      const content = serializeTool(tool);
      setJsonContent(content);

      // Store initial parameters for comparison/protection
      const params = typeof tool.parameters === 'string' ? JSON.parse(tool.parameters || '[]') : (tool.parameters || []);
      setInitialParams(params);

      setIsJsonMode(true);
    } else {
      // Switching TO Form mode
      if (jsonError) return; // Prevent switching if there are errors
      try {
        const parsed = JSON.parse(jsonContent);
        setEditingTool({
          ...editingTool, // Preserve ID and other internal metadata
          ...parsed,
          headers: typeof parsed.headers === 'string' ? parsed.headers : JSON.stringify(parsed.headers || {}),
          parameters: parsed.parameters || []
        });
        setIsJsonMode(false);
      } catch (e) {
        showToast("Cannot switch to Form view: Invalid JSON content.", "error");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[360px]">
        <div className="w-8 h-8 border-2 border-zinc-200 border-t-zinc-900 rounded-full animate-spin mb-3" />
        <p className="text-xs font-semibold text-zinc-400">Loading tools...</p>
      </div>
    );
  }

  const renderToolsList = () => {
    return (
      <div className="space-y-6">
        {/* Header & Controls Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">API Tools</h1>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Empower your AI assistants with live REST endpoints and MCP server capabilities.
            </p>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-auto">
            {/* View Switcher Tabs */}
            <div className="flex items-center bg-zinc-100 p-0.5 rounded-lg border border-zinc-200">
              <button
                onClick={() => {
                  setIsMcpMode(false);
                  setSearchTerm("");
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  !isMcpMode ? "bg-white text-zinc-900 shadow-xs" : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                Custom APIs
              </button>
              <button
                onClick={() => {
                  setIsMcpMode(true);
                  setSearchTerm("");
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  isMcpMode ? "bg-white text-zinc-900 shadow-xs" : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                MCP Hub
              </button>
            </div>

            {!isMcpMode ? (
              <button
                onClick={() => {
                  setEditingTool(null);
                  setIsAdding(true);
                  setIsJsonMode(false);
                  setUiError(null);
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 text-xs font-medium transition-colors"
              >
                <Plus size={14} />
                <span>Create Tool</span>
              </button>
            ) : (
              <button
                onClick={() => {
                  setIsAddingMcp(true);
                  setUiError(null);
                }}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 text-xs font-medium transition-colors"
              >
                <Plus size={14} />
                <span>Add MCP Server</span>
              </button>
            )}
          </div>
        </div>

        {isMcpMode ? (
          renderMcpView()
        ) : (
          <>
            {/* Search Input Bar */}
            <div className="relative w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={14} />
              <input
                type="text"
                placeholder="Search tools by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs bg-zinc-50/50 border border-zinc-200 rounded-lg focus:outline-none focus:bg-white focus:border-zinc-900 transition-colors text-zinc-850"
              />
            </div>

            {/* Tools Cards Grid */}
            {filteredTools.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTools.map((tool) => (
                  <div
                    key={tool.id}
                    onClick={() => handleEdit(tool)}
                    className="bg-white rounded-xl border border-zinc-200/90 p-4 sm:p-5 hover:border-zinc-300 hover:shadow-xs transition-all cursor-pointer flex flex-col justify-between group"
                  >
                    <div>
                      {/* Top status & method */}
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wider uppercase border ${
                          tool.method === 'POST' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          tool.method === 'PUT' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          tool.method === 'DELETE' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                          'bg-emerald-50 text-emerald-700 border-emerald-200'
                        }`}>
                          {tool.method || "GET"}
                        </span>

                        <span className="text-[10px] font-mono text-zinc-400 truncate max-w-[140px]" title={tool.url}>
                          {getHostname(tool.url)}
                        </span>
                      </div>

                      {/* Tool Title & Description */}
                      <h3 className="text-sm font-semibold text-zinc-900 group-hover:text-black transition-colors line-clamp-1">
                        {tool.name}
                      </h3>
                      <p className="text-xs text-zinc-500 mt-1 line-clamp-2 leading-relaxed">
                        {tool.description || "No description provided."}
                      </p>

                      {/* Badges / Metrics */}
                      <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-2 border-t border-zinc-100">
                        <span className="text-[10px] font-medium bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded">
                          {Array.isArray(tool.parameters) ? tool.parameters.length : 0} params
                        </span>
                        {tool.otp_enabled && (
                          <span className="text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">
                            OTP Active
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Bottom Action Footer */}
                    <div className="flex items-center justify-between border-t border-zinc-100 mt-4 pt-3">
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggle(tool);
                        }}
                        className="flex items-center gap-2 cursor-pointer select-none"
                      >
                        <div
                          className={`w-7 h-4 rounded-full transition-colors relative ${
                            tool.is_active ? "bg-zinc-900" : "bg-zinc-200"
                          }`}
                        >
                          <div
                            className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                              tool.is_active ? "left-3.5" : "left-0.5"
                            }`}
                          />
                        </div>
                        <span className={`text-[10px] font-medium ${
                          tool.is_active ? "text-emerald-600" : "text-zinc-400"
                        }`}>
                          {tool.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(tool);
                          }}
                          className="px-2 py-1 text-xs font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded transition-colors flex items-center gap-1"
                        >
                          <Edit2 size={12} />
                          <span>Edit</span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(tool.id);
                          }}
                          className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete tool"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center max-w-md mx-auto">
                <Wrench className="text-zinc-400 shrink-0" size={18} />
                <h3 className="text-sm font-semibold text-zinc-900">No tools configured</h3>
                <p className="text-xs text-zinc-500 mt-1 max-w-[260px]">
                  Create custom API tools so that your AI agents can perform live actions and lookups.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderEditor = () => {
    const tool = editingTool || emptyTool;
    return (
      <div className="space-y-6">
        {/* Editor Top Bar - Responsive Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-zinc-200">
          <button
            onClick={() => { setEditingTool(null); setIsAdding(false); }}
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors text-xs font-semibold self-start"
          >
            <ArrowLeft size={15} />
            <span>Back to Tools</span>
          </button>

          <div className="flex flex-wrap items-center gap-2">
            {editingTool?.id && (
              <button
                onClick={() => {
                  const initialInputs = {};
                  const initialLengths = {};
                  const initParams = (paramsList, parentPath = []) => {
                    paramsList.forEach(p => {
                      if (!p.name) return;
                      const fullPath = [...parentPath, p.name].join('.');
                      if (p.type === 'object') {
                        initParams(p.properties || [], [...parentPath, p.name]);
                      } else if (p.type === 'array') {
                        initialLengths[fullPath] = 1;
                        const subProperties = p.items?.properties || [];
                        if (subProperties.length > 0) {
                          initParams(subProperties, [...parentPath, p.name, 0]);
                        } else {
                          initialInputs[`${fullPath}.0`] = '';
                        }
                      } else {
                        initialInputs[fullPath] = p.value_type === 'constant' ? (p.constant_value || '') : '';
                      }
                    });
                  };
                  initParams(tool.parameters || []);
                  setTestInputs(initialInputs);
                  setArrayLengths(initialLengths);
                  setTestResult(null);
                  setIsTestModalOpen(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-700 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <Play size={13} />
                <span>Test Tool</span>
              </button>
            )}

            {!isJsonMode && (
              <button
                onClick={handleToggleJsonMode}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-700 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <Code size={13} />
                <span>JSON Mode</span>
              </button>
            )}

            {editingTool?.id && (
              <button
                onClick={() => {
                  setEditingTool(null);
                  setIsAdding(true);
                  setIsJsonMode(false);
                  setUiError(null);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-700 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <Plus size={13} />
                <span>New Tool</span>
              </button>
            )}

            <button
              disabled={!tool.name || (!isJsonMode && !tool.url)}
              onClick={() => handleSave(tool)}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 text-xs font-medium transition-colors disabled:opacity-40 disabled:pointer-events-none"
            >
              <Wrench size={13} />
              <span>{editingTool?.id ? (isJsonMode ? "Save JSON" : "Save Changes") : "Create Tool"}</span>
            </button>
          </div>
        </div>

        {isJsonMode ? (
          <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                  <Code size={15} className="text-zinc-600" />
                  JSON Configuration
                </h2>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Direct raw JSON specification for this API tool.
                </p>
              </div>
              <button
                onClick={handleToggleJsonMode}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors self-start sm:self-auto"
              >
                <Database size={13} />
                <span>Visual Form</span>
              </button>
            </div>

            <div className="rounded-lg border border-zinc-200 bg-zinc-950 p-4">
              <textarea
                value={jsonContent}
                onChange={(e) => setJsonContent(e.target.value)}
                className="w-full text-xs font-mono bg-transparent text-zinc-100 focus:outline-none min-h-[460px] leading-relaxed resize-y"
                spellCheck="false"
              />
            </div>

            {(jsonError || uiError) && (
              <div className="flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-lg text-red-600">
                <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
                <div className="text-xs space-y-1">
                  <p className="font-semibold">{jsonError || uiError}</p>
                  <p className="text-red-500">Ensure all keys and syntax adhere to valid JSON specification.</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* 2-Column Responsive Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Endpoint Details */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
                <div className="flex items-center gap-2 border-b border-zinc-100 pb-3">
                  <Globe size={15} className="text-zinc-600" />
                  <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Endpoint Settings</h2>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="sm:col-span-1">
                      <CustomSelect
                        label="HTTP Method"
                        value={tool.method}
                        onChange={handleMethodChange}
                        options={[
                          { label: "GET", value: "GET" },
                          { label: "POST", value: "POST" },
                          { label: "PUT", value: "PUT" },
                          { label: "DELETE", value: "DELETE" }
                        ]}
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <label className="block text-xs font-medium text-zinc-600 mb-1.5">Service URL</label>
                      <input
                        placeholder="https://api.yourdomain.com/endpoint"
                        value={tool.url}
                        onChange={(e) => setEditingTool({ ...tool, url: e.target.value })}
                        className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:outline-none focus:bg-white focus:border-zinc-900 transition-colors font-mono text-zinc-900"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1.5">Tool Name</label>
                    <input
                      placeholder="e.g. check_order_status"
                      value={tool.name}
                      onChange={(e) => setEditingTool({ ...tool, name: e.target.value })}
                      className="w-full px-3 py-2 text-xs font-medium border border-zinc-200 rounded-lg bg-zinc-50/50 focus:outline-none focus:bg-white focus:border-zinc-900 transition-colors text-zinc-900"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1.5">
                      Tool Description <span className="text-zinc-400 font-normal">(LLM Prompt Guidance)</span>
                    </label>
                    <textarea
                      placeholder="Describe purpose and required arguments so the AI knows when and how to invoke this tool..."
                      value={tool.description}
                      rows={3}
                      onChange={(e) => setEditingTool({ ...tool, description: e.target.value })}
                      className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:outline-none focus:bg-white focus:border-zinc-900 transition-colors text-zinc-800 resize-none leading-relaxed"
                    />
                  </div>
                </div>
              </div>

              {/* Custom Headers */}
              <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-4">
                <div className="flex items-center gap-2 border-b border-zinc-100 pb-3">
                  <Shield size={15} className="text-zinc-600" />
                  <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Custom Headers</h2>
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-zinc-500 mb-1.5">JSON Key-Value Pairs</label>
                  <textarea
                    placeholder='{\n  "Authorization": "Bearer token"\n}'
                    value={typeof tool.headers === 'string' ? tool.headers : JSON.stringify(tool.headers || {}, null, 2)}
                    onChange={(e) => setEditingTool({ ...tool, headers: e.target.value })}
                    rows={7}
                    className="w-full px-3 py-2.5 text-[11px] font-mono border border-zinc-200 rounded-lg bg-zinc-50/50 focus:outline-none focus:bg-white focus:border-zinc-900 transition-colors resize-none leading-relaxed text-zinc-800"
                  />
                  <p className="text-[11px] text-zinc-400 mt-2 flex items-center gap-1">
                    <Info size={12} className="flex-shrink-0" />
                    <span>Keys and values must be enclosed in double quotes.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Payload Schema Configuration */}
            <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
              <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
                <div className="flex items-center gap-2">
                  <Database size={15} className="text-zinc-600" />
                  <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Payload Parameters</h2>
                </div>
                <button
                  type="button"
                  onClick={addParameter}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 text-xs font-medium transition-colors"
                >
                  <Plus size={13} />
                  <span>Add Parameter</span>
                </button>
              </div>

              <div className="space-y-3">
                {tool.parameters?.length > 0 ? (
                  tool.parameters.map((param, index) => (
                    <ParameterRow
                      key={index}
                      parameter={param}
                      path={[index]}
                      onUpdate={handleUpdateParameter}
                      onRemove={handleRemoveParameter}
                    />
                  ))
                ) : (
                  <div className="py-8 text-center">
                    <p className="text-xs font-medium text-zinc-500">No parameters defined</p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">Click "Add Parameter" to configure arguments passed to this endpoint.</p>
                  </div>
                )}
              </div>
            </div>

            {/* OTP Verification Configuration */}
            <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
              <div className="flex items-center justify-between border-b border-zinc-100 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <KeyRound size={15} className={tool.otp_enabled ? "text-emerald-600" : "text-zinc-500"} />
                    <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">OTP Verification</h2>
                    {tool.otp_enabled && (
                      <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-medium border border-emerald-200">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Transforms user chat input into digits PIN boxes to validate via external API before execution.
                  </p>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={Boolean(tool.otp_enabled)}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setEditingTool({
                        ...tool,
                        otp_enabled: enabled,
                        otp_config: tool.otp_config || {
                          digits: 4,
                          verify_url: "",
                          verify_headers: {},
                          verify_body_template: { otp: "{{otp}}" },
                          context_mappings: {},
                          verify_success: {
                            type: "json_path",
                            path: "success",
                            expected: true
                          },
                          max_attempts: 3
                        }
                      });
                    }}
                  />
                  <div className="w-10 h-5 bg-zinc-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>

              {tool.otp_enabled && (
                <div className="space-y-4 pt-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-zinc-600 mb-1.5">PIN Box Digits</label>
                      <div className="flex gap-2">
                        {[4, 6].map(num => (
                          <button
                            key={num}
                            type="button"
                            onClick={() => setEditingTool({
                              ...tool,
                              otp_config: { ...(tool.otp_config || {}), digits: num }
                            })}
                            className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-medium border transition-colors ${
                              (tool.otp_config?.digits || 4) === num
                                ? "bg-zinc-900 text-white border-zinc-900"
                                : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                            }`}
                          >
                            {num} Digits {num === 4 ? "(Standard)" : ""}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-zinc-600 mb-1.5">Max Attempts</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={tool.otp_config?.max_attempts ?? 3}
                        onChange={(e) => setEditingTool({
                          ...tool,
                          otp_config: { ...(tool.otp_config || {}), max_attempts: parseInt(e.target.value, 10) || 3 }
                        })}
                        className="w-full px-3 py-1.5 text-xs border border-zinc-200 rounded-lg bg-white focus:outline-none focus:border-zinc-900 text-zinc-900"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1.5">
                      Verification Endpoint URL <span className="text-zinc-400 font-normal">(POST)</span>
                    </label>
                    <input
                      type="url"
                      placeholder="https://api.yourdomain.com/auth/verify-otp"
                      value={tool.otp_config?.verify_url || ""}
                      onChange={(e) => setEditingTool({
                        ...tool,
                        otp_config: { ...(tool.otp_config || {}), verify_url: e.target.value }
                      })}
                      className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 font-mono text-zinc-900"
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-zinc-600 mb-1.5">
                        Verification Headers (JSON)
                      </label>
                      <textarea
                        rows={4}
                        placeholder='{\n  "Authorization": "Bearer SECRET_KEY"\n}'
                        value={
                          typeof tool.otp_config?.verify_headers === 'string'
                            ? tool.otp_config.verify_headers
                            : JSON.stringify(tool.otp_config?.verify_headers || {}, null, 2)
                        }
                        onChange={(e) => {
                          setEditingTool({
                            ...tool,
                            otp_config: { ...(tool.otp_config || {}), verify_headers: e.target.value }
                          });
                        }}
                        className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 font-mono resize-none text-zinc-800"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-medium text-zinc-600">
                          Request Payload (JSON)
                        </label>
                        <div className="flex items-center gap-1">
                          {['{{otp}}', '{{phone}}', '{{verification_id}}'].map(tag => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => {
                                const currentTpl = typeof tool.otp_config?.verify_body_template === 'string'
                                  ? tool.otp_config.verify_body_template
                                  : JSON.stringify(tool.otp_config?.verify_body_template || { otp: "{{otp}}" }, null, 2);
                                const updated = currentTpl.includes(tag) ? currentTpl : currentTpl.replace(/\}$/, `  "${tag.replace(/[{}]/g, '')}": "${tag}"\n}`);
                                setEditingTool({
                                  ...tool,
                                  otp_config: { ...(tool.otp_config || {}), verify_body_template: updated }
                                });
                              }}
                              className="px-1.5 py-0.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded text-[10px] font-mono"
                            >
                              + {tag}
                            </button>
                          ))}
                        </div>
                      </div>
                      <textarea
                        rows={4}
                        placeholder='{\n  "otp": "{{otp}}"\n}'
                        value={
                          typeof tool.otp_config?.verify_body_template === 'string'
                            ? tool.otp_config.verify_body_template
                            : JSON.stringify(tool.otp_config?.verify_body_template || { otp: "{{otp}}" }, null, 2)
                        }
                        onChange={(e) => {
                          setEditingTool({
                            ...tool,
                            otp_config: { ...(tool.otp_config || {}), verify_body_template: e.target.value }
                          });
                        }}
                        className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 font-mono resize-none text-zinc-800"
                      />
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-zinc-50 border border-zinc-200 space-y-3">
                    <label className="block text-xs font-semibold text-zinc-900">Success Evaluation Rule</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-medium text-zinc-500 mb-1">Rule Type</label>
                        <select
                          value={tool.otp_config?.verify_success?.type || "json_path"}
                          onChange={(e) => setEditingTool({
                            ...tool,
                            otp_config: {
                              ...(tool.otp_config || {}),
                              verify_success: {
                                ...(tool.otp_config?.verify_success || {}),
                                type: e.target.value
                              }
                            }
                          })}
                          className="w-full px-2.5 py-1.5 text-xs border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:border-zinc-900"
                        >
                          <option value="json_path">Match Property Path</option>
                          <option value="boolean">Direct Boolean Response</option>
                        </select>
                      </div>

                      {tool.otp_config?.verify_success?.type !== "boolean" && (
                        <div>
                          <label className="block text-[11px] font-medium text-zinc-500 mb-1">JSON Path</label>
                          <input
                            type="text"
                            placeholder="success"
                            value={tool.otp_config?.verify_success?.path || "success"}
                            onChange={(e) => setEditingTool({
                              ...tool,
                              otp_config: {
                                ...(tool.otp_config || {}),
                                verify_success: {
                                  ...(tool.otp_config?.verify_success || {}),
                                  path: e.target.value
                                }
                              }
                            })}
                            className="w-full px-2.5 py-1.5 text-xs border border-zinc-200 rounded-lg bg-white font-mono text-zinc-900 focus:outline-none focus:border-zinc-900"
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-[11px] font-medium text-zinc-500 mb-1">Expected Value</label>
                        <input
                          type="text"
                          placeholder="true"
                          value={String(tool.otp_config?.verify_success?.expected ?? true)}
                          onChange={(e) => {
                            const val = e.target.value;
                            const boolVal = val.toLowerCase() === 'true' ? true : (val.toLowerCase() === 'false' ? false : val);
                            setEditingTool({
                              ...tool,
                              otp_config: {
                                ...(tool.otp_config || {}),
                                verify_success: {
                                  ...(tool.otp_config?.verify_success || {}),
                                  expected: boolVal
                                }
                              }
                            });
                          }}
                          className="w-full px-2.5 py-1.5 text-xs border border-zinc-200 rounded-lg bg-white font-mono text-emerald-600 font-semibold focus:outline-none focus:border-zinc-900"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderMcpView = () => {
    const filteredMcpServers = mcpServers.filter((server) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      const matchesUrl = server.url?.toLowerCase().includes(term);
      const matchesTool = (server.tools || []).some(t => t.tool_name?.toLowerCase().includes(term));
      return matchesUrl || matchesTool;
    });

    return (
      <div className="space-y-6">
        {/* Discovery Box - shown only when isAddingMcp is true */}
        {isAddingMcp && (
          <div className="bg-white rounded-xl border border-zinc-200 p-5 sm:p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider flex items-center gap-2">
                <Cpu size={14} className="text-zinc-600" />
                <span>Discover MCP Server</span>
              </h3>
              <button
                onClick={() => { setIsAddingMcp(false); setMcpDiscovery({ url: "", headers: "{}", tools: [] }); setUiError(null); }}
                className="p-1 text-zinc-400 hover:text-zinc-700 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {uiError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs flex items-center gap-2">
                <AlertCircle size={14} className="flex-shrink-0" />
                <span>{uiError}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1.5">Server URL (SSE)</label>
                <input
                  value={mcpDiscovery.url}
                  onChange={(e) => setMcpDiscovery(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://example.com/mcp"
                  className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 font-mono text-zinc-900"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1.5">Custom Headers (JSON)</label>
                <input
                  value={mcpDiscovery.headers}
                  onChange={(e) => setMcpDiscovery(prev => ({ ...prev, headers: e.target.value }))}
                  placeholder='{"Authorization": "BearerToken"}'
                  className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 font-mono text-zinc-900"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setIsAddingMcp(false); setMcpDiscovery({ url: "", headers: "{}", tools: [] }); setUiError(null); }}
                className="px-3 py-1.5 text-xs font-medium text-zinc-600 hover:text-zinc-900 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDiscoverMcp}
                disabled={isDiscovering || !mcpDiscovery.url}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 text-xs font-medium transition-colors disabled:opacity-40"
              >
                {isDiscovering ? (
                  <RotateCcw size={13} className="animate-spin" />
                ) : (
                  <Search size={13} />
                )}
                <span>{isDiscovering ? "Discovering..." : "Scan for Tools"}</span>
              </button>
            </div>

            {mcpDiscovery.tools.length > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-100 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">
                    Available Tools ({mcpDiscovery.tools.length})
                  </h4>
                  <p className="text-[11px] text-zinc-500">Enable tools to register</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {mcpDiscovery.tools.map((tool, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                      <div className="flex items-center gap-2 min-w-0 mr-2">
                        <Layers size={13} className="text-zinc-500 flex-shrink-0" />
                        <span className="text-xs font-medium text-zinc-900 truncate">{tool.tool_name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newTools = [...mcpDiscovery.tools];
                          newTools[idx].is_active = !newTools[idx].is_active;
                          setMcpDiscovery(prev => ({ ...prev, tools: newTools }));
                        }}
                        className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${
                          tool.is_active ? "bg-zinc-900" : "bg-zinc-200"
                        }`}
                      >
                        <div
                          className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                            tool.is_active ? "left-4" : "left-0.5"
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleSaveMcp}
                    className="px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 text-xs font-medium transition-colors"
                  >
                    Save MCP Configuration
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content View: Empty State OR Search + Servers List */}
        {mcpServers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center max-w-md mx-auto">
            <Cpu className="text-zinc-400 shrink-0" size={18} />
            <h3 className="text-sm font-semibold text-zinc-900">No MCP connections configured</h3>
            <p className="text-xs text-zinc-500 mt-1 max-w-[260px]">
              Connect external Model Context Protocol endpoints to discover and empower your AI assistants with remote capabilities.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Search Input Bar */}
            <div className="relative w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={14} />
              <input
                type="text"
                placeholder="Search MCP servers or tools..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs bg-zinc-50/50 border border-zinc-200 rounded-lg focus:outline-none focus:bg-white focus:border-zinc-900 transition-colors text-zinc-850"
              />
            </div>

            {filteredMcpServers.length > 0 ? (
              <div className="grid grid-cols-1 gap-3">
                {filteredMcpServers.map((server) => (
                  <div key={server.id} className="bg-white rounded-xl border border-zinc-200 p-5 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-zinc-100 pb-3">
                      <div className="flex items-center gap-3">
                        <Cpu className="text-zinc-700 shrink-0" size={18} />
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-xs font-semibold text-zinc-900 font-mono break-all">{server.url}</h3>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                              server.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-zinc-50 text-zinc-500 border-zinc-200'
                            }`}>
                              {server.is_active ? 'Active' : 'Suspended'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 self-end sm:self-auto">
                        <button
                          onClick={() => toggleMcpServer(server.id, server.is_active)}
                          className="px-2.5 py-1 text-xs font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-md transition-colors"
                        >
                          {server.is_active ? 'Suspend' : 'Resume'}
                        </button>
                        <button
                          onClick={() => deleteMcpServer(server.id)}
                          className="p-1.5 text-zinc-400 hover:text-red-600 rounded-md transition-colors"
                          title="Delete connection"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                      {(server.tools || []).map((tool) => (
                        <div key={tool.id} className="flex items-center justify-between px-3 py-2 bg-zinc-50 rounded-lg border border-zinc-150">
                          <span className={`text-xs font-medium truncate mr-2 ${tool.is_active ? 'text-zinc-900' : 'text-zinc-400'}`}>
                            {tool.tool_name}
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleMcpTool(tool.id, tool.is_active)}
                            className={`w-7 h-4 rounded-full transition-colors relative flex-shrink-0 ${
                              tool.is_active ? "bg-zinc-900" : "bg-zinc-200"
                            }`}
                          >
                            <div
                              className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                                tool.is_active ? "left-3.5" : "left-0.5"
                              }`}
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 bg-white rounded-xl border border-zinc-200">
                <p className="text-xs text-zinc-400">No MCP connections match &quot;{searchTerm}&quot;</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-6">
        {isAdding || editingTool ? renderEditor() : renderToolsList()}

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDelete}
          isLoading={isDeleting}
          title="Delete Tool?"
          message="Are you sure you want to remove this tool capability? This action cannot be undone."
        />

        <DeleteConfirmModal
          isOpen={mcpServerToDelete !== null}
          onClose={() => setMcpServerToDelete(null)}
          onConfirm={confirmDeleteMcp}
          isLoading={isDeletingMcp}
          title="Disconnect MCP Server?"
          message="This will remove the server and disable all associated tools. Proceed?"
        />

        {/* 🚀 TEST MODAL - Minimalist & Mobile Responsive */}
        {isTestModalOpen && ReactDOM.createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-2.5 sm:p-5">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
              onClick={() => !isTestLoading && setIsTestModalOpen(false)}
            />

            <div className="relative w-full max-w-4xl bg-white rounded-xl sm:rounded-2xl shadow-xl border border-zinc-200 overflow-hidden flex flex-col max-h-[94vh]">
              {/* Modal Header */}
              <div className="px-4 py-3.5 sm:px-5 sm:py-4 border-b border-zinc-200 flex items-center justify-between bg-white">
                <div>
                  <h3 className="text-sm font-semibold text-zinc-900">Test Tool Execution</h3>
                  <p className="text-[11px] sm:text-xs text-zinc-500 mt-0.5">Test parameter arguments against your API endpoint.</p>
                </div>
                <button
                  onClick={() => setIsTestModalOpen(false)}
                  className="p-1 text-zinc-400 hover:text-zinc-700 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Modal Body - 2 Columns */}
              <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 overflow-y-auto">
                {/* Left: Parameter inputs */}
                <div className="p-4 sm:p-5 border-b md:border-b-0 md:border-r border-zinc-200 space-y-4 sm:space-y-5 overflow-y-auto min-h-0 max-h-[360px] md:max-h-[500px]">
                  {(() => {
                    const renderParam = (param, parentPath = []) => {
                      const fullPathArr = [...parentPath, param.name];
                      const fullPath = fullPathArr.join('.');

                      if (param.type === 'object') {
                        const subProperties = param.properties || [];
                        return (
                          <div key={fullPath} className="space-y-3 border-l-2 border-zinc-200 pl-3 py-1 bg-zinc-50/50 rounded-r-lg">
                            <span className="text-xs font-semibold text-zinc-700">{param.name} (Object)</span>
                            {param.description && (
                              <p className="text-[11px] text-zinc-500">{param.description}</p>
                            )}
                            <div className="space-y-3">
                              {subProperties.map(subParam => renderParam(subParam, fullPathArr))}
                            </div>
                          </div>
                        );
                      }

                      if (param.type === 'array') {
                        const subProperties = param.items?.properties || [];
                        const length = arrayLengths[fullPath] || 0;
                        const indexes = Array.from({ length }, (_, i) => i);

                        return (
                          <div key={fullPath} className="space-y-3 border-l-2 border-zinc-200 pl-3 py-1 bg-zinc-50/50 rounded-r-lg">
                            <span className="text-xs font-semibold text-zinc-700">{param.name} (Array)</span>
                            {param.description && (
                              <p className="text-[11px] text-zinc-500">{param.description}</p>
                            )}
                            <div className="space-y-3">
                              {indexes.map((idx) => (
                                <div key={idx} className="border border-dashed border-zinc-200 p-3 rounded-lg space-y-2">
                                  <span className="text-[10px] font-medium text-zinc-400 uppercase">Item #{idx + 1}</span>
                                  {subProperties.map(subParam => renderParam(subParam, [...fullPathArr, idx]))}
                                  {subProperties.length === 0 && (
                                    <input
                                      placeholder={`Item #${idx + 1} value`}
                                      className="w-full px-2.5 py-1.5 text-xs border border-zinc-200 rounded-md bg-white focus:outline-none focus:border-zinc-900"
                                      value={testInputs[`${fullPath}.${idx}`] || ''}
                                      onChange={(e) => {
                                        setTestInputs({ ...testInputs, [`${fullPath}.${idx}`]: e.target.value });
                                      }}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={fullPath} className="space-y-1">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-zinc-700">{fullPath}</label>
                            {param.value_type === 'constant' && (
                              <span className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded font-medium">
                                Constant
                              </span>
                            )}
                          </div>
                          <input
                            placeholder={param.name}
                            readOnly={param.value_type === 'constant'}
                            className={`w-full px-3 py-1.5 text-xs border rounded-lg transition-colors ${
                              param.value_type === 'constant'
                                ? "bg-zinc-100 border-zinc-200 text-zinc-400 cursor-not-allowed"
                                : "bg-white border-zinc-200 text-zinc-900 focus:outline-none focus:border-zinc-900"
                            }`}
                            value={testInputs[fullPath] || ''}
                            onChange={(e) => {
                              if (param.value_type !== 'constant') {
                                setTestInputs({ ...testInputs, [fullPath]: e.target.value });
                              }
                            }}
                          />
                          {param.description && (
                            <p className="text-[11px] text-zinc-400">{param.description}</p>
                          )}
                        </div>
                      );
                    };

                    return ['path', 'query', 'body'].map((loc) => {
                      const locParams = (editingTool?.parameters || []).filter(p => p.name && (p.location || 'body') === loc);
                      if (locParams.length === 0) return null;

                      return (
                        <div key={loc} className="space-y-3">
                          <h4 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                            {loc === 'query' ? 'Query Parameters' : loc === 'path' ? 'Path Parameters' : 'Body Parameters'}
                          </h4>
                          <div className="space-y-3">
                            {locParams.map((param) => renderParam(param))}
                          </div>
                        </div>
                      );
                    });
                  })()}

                  {(editingTool?.parameters || []).filter(p => p.name).length === 0 && (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <CheckCircle2 size={24} className="text-zinc-400 mb-2" />
                      <p className="text-xs text-zinc-600 font-medium">No inputs required</p>
                      <p className="text-[11px] text-zinc-400 mt-0.5">This tool has no parameterized arguments.</p>
                    </div>
                  )}
                </div>

                {/* Right: Execution Result */}
                <div className="p-4 sm:p-5 bg-zinc-50/50 space-y-4 overflow-y-auto min-h-0 max-h-[360px] md:max-h-[500px]">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold text-zinc-700">Response Payload</h4>
                    {testResult && (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                        testResult.status_code >= 200 && testResult.status_code < 300
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-red-50 text-red-700 border-red-200"
                      }`}>
                        Status: {testResult.status_code || "Response"}
                      </span>
                    )}
                  </div>

                  <div className="rounded-lg border border-zinc-200 bg-zinc-900 p-3 min-h-[160px] text-zinc-100 font-mono text-xs overflow-x-auto">
                    {testResult ? (
                      <pre className="whitespace-pre-wrap break-all leading-relaxed">
                        {JSON.stringify(testResult.data || testResult, null, 2)}
                      </pre>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full min-h-[140px] text-zinc-500 text-center">
                        <Database size={24} className="mb-2 opacity-50" />
                        <p className="text-xs">Execute tool to view output</p>
                      </div>
                    )}
                  </div>

                  {/* Raw Request Collapsible */}
                  <div className="border-t border-zinc-200 pt-3">
                    <button
                      type="button"
                      onClick={() => setIsRawRequestOpen(!isRawRequestOpen)}
                      className="w-full flex items-center justify-between text-xs text-zinc-600 hover:text-zinc-900 py-1"
                    >
                      <span className="font-medium">Inspect Raw Request</span>
                      <ChevronDown size={14} className={`transition-transform ${isRawRequestOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isRawRequestOpen && (
                      <div className="mt-2 rounded-lg border border-zinc-200 bg-zinc-900 p-3 overflow-x-auto">
                        <pre className="text-[11px] font-mono text-zinc-300 leading-relaxed whitespace-pre">
                          {testResult?.raw_request || "No request data available yet."}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-4 py-3 sm:px-5 sm:py-3 border-t border-zinc-200 bg-white flex flex-wrap items-center justify-end gap-2">
                <button
                  onClick={() => setIsTestModalOpen(false)}
                  className="px-3.5 py-1.5 text-xs font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={isTestLoading}
                  onClick={async () => {
                    setIsTestLoading(true);
                    try {
                      // Convert flat dot-notation inputs back to nested structure
                      const nestedInputs = {};
                      Object.entries(testInputs).forEach(([key, val]) => {
                        const parts = key.split('.');
                        let current = nestedInputs;
                        for (let i = 0; i < parts.length; i++) {
                          const part = parts[i];
                          const nextPart = parts[i + 1];
                          const isNextPartIndex = nextPart !== undefined && /^\d+$/.test(nextPart);
                          const isCurrentArray = Array.isArray(current);
                          const keyOrIdx = isCurrentArray ? parseInt(part, 10) : part;

                          if (i === parts.length - 1) {
                            current[keyOrIdx] = val;
                          } else {
                            const isNextPartArray = isNextPartIndex;
                            if (!current[keyOrIdx]) {
                              current[keyOrIdx] = isNextPartArray ? [] : {};
                            }
                            current = current[keyOrIdx];
                          }
                        }
                      });

                      const res = await apiClient.testTool(editingTool.id, nestedInputs);
                      setTestResult(res);
                    } catch (err) {
                      setTestResult({ error: err.message, status_code: 500 });
                    } finally {
                      setIsTestLoading(false);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 text-xs font-medium transition-colors disabled:opacity-40"
                >
                  {isTestLoading && <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                  <span>{isTestLoading ? "Testing..." : "Send Request"}</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default ApiToolsManagement;
